import React from "react";
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Circle,
  Grid,
  GridItem,
  Heading,
  HStack,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  ModalFooter,
  SimpleGrid,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  VStack,
  useColorModeValue,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { FiArrowUpRight, FiGift } from "react-icons/fi";

import { HeroTexture } from "../components/common/HeroTexture";
import { DataTable } from "../components/DataTable";
import { InvestmentPaymentCard } from "../components/common/InvestmentPaymentCard";
import { PaymentStatusBadge } from "../components/common/PaymentCardVisual";
import { ROUTE_PATHS } from "../config/routes";
import { club, paymentCards } from "../services/api";

const POINT_GUIDE_ROWS = [
  { activity: "اولین سرمایه گذاری", points: "۱۰,۰۰۰ امتیاز" },
  { activity: "هر روز سرمایه گذاری به مبلغ x", points: "x/365*0.004" },
  { activity: "نگهداری سرمایه بیش از ۳ ماه", points: "x/365*0.005" },
  { activity: "نگهداری سرمایه بیش از ۶ ماه", points: "x/365*0.006" },
];

const GENERAL_RULES = [
  "امتیازها غیرقابل انتقال هستند.",
  "اعتبار امتیازها ۱۲ ماه است.",
  "پس از ثبت نام موفق و تکمیل احراز هویت کاربر دعوت شده، ۵,۰۰۰ امتیاز خوش آمدگویی به کاربر جدید تعلق می گیرد.",
  "پس از معرفی موفق، ۱۰,۰۰۰ امتیاز به معرف تعلق می گیرد.",
  "اگر کاربر دعوت شده سرمایه گذاری انجام دهد، امتیاز سرمایه گذاری او مطابق قوانین باشگاه برای خودش ثبت می شود و ۱۰٪ از همان امتیاز نیز به معرف تعلق می گیرد.",
  "شرکت می تواند قوانین باشگاه را در آینده به روزرسانی کند.",
  "در صورت سوءاستفاده از سیستم معرفی، امتیازهای مربوطه حذف خواهند شد.",
];

const formatPoints = (value?: number) =>
  Number(value || 0).toLocaleString("fa-IR");

const formatRial = (value?: number) =>
  `${Number(value || 0).toLocaleString("fa-IR")} ریال`;

const formatDate = (value?: string | Date | null) =>
  value ? new Date(value).toLocaleDateString("fa-IR") : "—";

const formatCardNumber = (value?: string | null) =>
  String(value || "")
    .replace(/[^\d]/g, "")
    .replace(/(\d{4})(?=\d)/g, "$1-");

const rewardStatusMeta: Record<string, { label: string; color: string }> = {
  fulfilled: { label: "تحویل شده", color: "green" },
  pending: { label: "در انتظار", color: "orange" },
  failed: { label: "ناموفق", color: "red" },
};

const pointTypeMeta: Record<string, { label: string; color: string }> = {
  earned: { label: "کسب", color: "green" },
  spent: { label: "مصرف", color: "orange" },
  expired: { label: "منقضی", color: "red" },
  reversed: { label: "برگشت", color: "blue" },
  adjusted: { label: "اصلاح", color: "purple" },
};

const sourceLabelMap: Record<string, string> = {
  referral_inviter_bonus: "پاداش معرفی",
  referral_invitee_welcome: "خوش آمدگویی",
  first_investment: "اولین سرمایه گذاری",
  referral_investment_share: "سهم معرف",
  daily_investment: "امتیاز روزانه",
  holding_3m: "نگهداری بیش از ۳ ماه",
  holding_6m: "نگهداری بیش از ۶ ماه",
  reward_redemption: "بازخرید جایزه",
  reward_redemption_refund: "برگشت بازخرید",
  points_expiration: "انقضای امتیاز",
  manual_adjustment: "اصلاح دستی",
};

const cardStatusMeta: Record<string, { label: string; colorScheme: string }> = {
  active: { label: "فعال", colorScheme: "green" },
  used: { label: "استفاده شده", colorScheme: "blue" },
  cancelled: { label: "لغو شده", colorScheme: "red" },
};

const Panel: React.FC<{
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, icon, children }) => {
  const bg = useColorModeValue("light.50", "dark.50");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const iconBg = useColorModeValue(
    "rgba(180,83,9,0.10)",
    "rgba(251,191,36,0.10)",
  );
  const iconColor = useColorModeValue("orange.600", "orange.200");

  return (
    <Box
      bg={bg}
      backdropFilter="blur(12px)"
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="2xl"
      p={{ base: 5, md: 6 }}
      // boxShadow="0 24px 60px rgba(15, 23, 42, 0.08)"
    >
      <HStack spacing={3} mb={5}>
        <Box
          w={12}
          h={12}
          borderRadius="2xl"
          display="flex"
          alignItems="center"
          justifyContent="center"
          bg={iconBg}
          color={iconColor}
        >
          {icon}
        </Box>
        <Heading size="md">{title}</Heading>
      </HStack>
      {children}
    </Box>
  );
};

export const ClubPage: React.FC = () => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const cardDetailsModal = useDisclosure();
  const pointGuideModal = useDisclosure();
  const rulesModal = useDisclosure();

  const [selectedCard, setSelectedCard] = React.useState<any>(null);
  const [isCardLoading, setIsCardLoading] = React.useState(false);
  const [latestClaimCardId, setLatestClaimCardId] = React.useState("");
  const [historyPage, setHistoryPage] = React.useState(1);
  const historyPageSize = 12;

  const mutedColor = useColorModeValue("gray.600", "gray.300");
  const heroBg = useColorModeValue(
    "linear-gradient(135deg, rgba(255,255,255,0.96) 0%, rgba(255,248,248,0.92) 35%, rgba(249,216,215,0.88) 100%)",
    "linear-gradient(135deg, rgba(23,36,61,0.96) 0%, rgba(19,32,54,0.98) 50%, rgba(138,9,5,0.28) 100%)",
  );
  const heroOverlay = useColorModeValue(
    "radial-gradient(circle at 12% 20%, rgba(255,255,255,0.95), transparent 30%), radial-gradient(circle at 85% 18%, rgba(249,168,166,0.42), transparent 34%), radial-gradient(circle at 78% 82%, rgba(255,194,0,0.14), transparent 22%)",
    "radial-gradient(circle at 18% 20%, rgba(255,255,255,0.08), transparent 26%), radial-gradient(circle at 86% 18%, rgba(249,168,166,0.16), transparent 32%), radial-gradient(circle at 78% 80%, rgba(255,194,0,0.1), transparent 20%)",
  );
  const heroTextColor = useColorModeValue("gray.900", "white");
  const softTextColor = useColorModeValue("gray.600", "gray.300");
  const subtleBadgeBg = useColorModeValue("whiteAlpha.800", "whiteAlpha.400");
  const subtleBadgeColor = useColorModeValue("orange.700", "white");
  const avatarRing = useColorModeValue(
    "rgba(255,255,255,0.96)",
    "whiteAlpha.160",
  );
  const cardBorderColor = useColorModeValue("gray.200", "gray.700");
  const subBoxBg = useColorModeValue("light.400", "dark.700");
  const claimCtaBg = useColorModeValue("orange.50", "rgba(251,191,36,0.10)");
  const claimCtaBorder = useColorModeValue("orange.200", "orange.700");
  const claimCtaTitle = useColorModeValue("orange.700", "orange.200");
  const heroPanelBorder = useColorModeValue("blackAlpha.100", "whiteAlpha.120");
  const heroPanelMuted = useColorModeValue("gray.500", "gray.400");
  const heroPanelCardBg = useColorModeValue("whiteAlpha.700", "whiteAlpha.60");
  const heroPanelCardHoverBg = useColorModeValue("white", "whiteAlpha.100");
  const historyPositiveColor = useColorModeValue("green.600", "green.300");
  const historyNegativeColor = useColorModeValue("red.500", "red.300");
  const emptyBorder = useColorModeValue("blackAlpha.100", "whiteAlpha.200");
  const secondaryTabBg = useColorModeValue("blackAlpha.50", "whiteAlpha.50");
  const secondaryTabBorder = useColorModeValue(
    "blackAlpha.100",
    "whiteAlpha.120",
  );
  const secondaryTabSelectedBg = useColorModeValue("white", "whiteAlpha.120");
  const secondaryTabSelectedColor = useColorModeValue("gray.900", "white");
  const secondaryTabShadow = useColorModeValue(
    "0 10px 24px rgba(15, 23, 42, 0.08)",
    "0 10px 24px rgba(0, 0, 0, 0.18)",
  );

  const meQuery = useQuery(["club", "me"], club.getMe);
  const historyQuery = useQuery(
    ["club", "history", historyPage],
    () => club.getHistory(historyPage, historyPageSize),
    {
      keepPreviousData: true,
    },
  );
  const rewardsQuery = useQuery(["club", "rewards"], club.getRewards);

  const isLoading =
    meQuery.isLoading || historyQuery.isLoading || rewardsQuery.isLoading;

  const isError =
    meQuery.isError || historyQuery.isError || rewardsQuery.isError;
  const me = meQuery.data?.data;
  const history = historyQuery.data?.history || [];
  const historyTotalPages = historyQuery.data?.totalPage || 0;
  const rewards = rewardsQuery.data?.rewards || [];
  const latestRedemptions = me?.latestRedemptions || [];
  const historyTotalData = historyQuery.data?.totalData || 0;
  const isPrivatePerson = me?.isPrivatePerson !== false;

  const historyColumns = React.useMemo(
    () => [
      {
        header: "تاریخ",
        accessor: "createdAt",
        render: (item: any) => formatDate(item.createdAt),
      },
      {
        header: "شرح",
        accessor: "source",
        render: (item: any) =>
          sourceLabelMap[item.source] || item.source || "—",
      },
      {
        header: "نوع",
        accessor: "type",
        render: (item: any) => {
          const pointMeta = pointTypeMeta[item.type] || {
            label: item.type || "—",
            color: "gray",
          };

          return <Badge colorScheme={pointMeta.color}>{pointMeta.label}</Badge>;
        },
      },
      {
        header: "امتیاز",
        accessor: "points",
        render: (item: any) => {
          const pointValue = Number(item.points || 0);

          return (
            <Text
              textAlign="right"
              color={
                pointValue >= 0 ? historyPositiveColor : historyNegativeColor
              }
              fontWeight="bold"
            >
              {pointValue >= 0 ? "+" : ""}
              {formatPoints(pointValue)}
            </Text>
          );
        },
      },
      {
        header: "انقضا",
        accessor: "expiresAt",
        render: (item: any) => formatDate(item.expiresAt),
      },
    ],
    [historyNegativeColor, historyPositiveColor],
  );

  const pointGuideColumns = React.useMemo(
    () => [
      { header: "فعالیت", accessor: "activity" },
      {
        header: "امتیاز",
        accessor: "points",
        render: (item: any) => (
          <Text textAlign="right" fontWeight="bold">
            {item.points}
          </Text>
        ),
      },
    ],
    [],
  );

  const handleCopy = async (value: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast({
        title: `${label} کپی شد`,
        status: "success",
        duration: 2500,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "کپی انجام نشد",
        description: "امکان کپی در مرورگر شما فراهم نیست.",
        status: "error",
        duration: 2500,
        isClosable: true,
      });
    }
  };

  const handleOpenCardDetails = async (cardId?: string | null) => {
    if (!cardId) {
      return;
    }

    try {
      setIsCardLoading(true);
      const fullCard = await paymentCards.getOne(cardId);
      setSelectedCard(fullCard);
      cardDetailsModal.onOpen();
    } catch (error: any) {
      toast({
        title: "جزئیات کارت هدیه دریافت نشد",
        description:
          error?.response?.data?.message || error?.message || "خطای ناشناخته",
        status: "error",
        duration: 3500,
        isClosable: true,
      });
    } finally {
      setIsCardLoading(false);
    }
  };

  const handleRedeem = async (rewardId: string) => {
    try {
      const response = await club.redeemReward(rewardId);
      const paymentCardId =
        response?.data?.paymentCard?._id || response?.data?.paymentCard || "";

      setLatestClaimCardId(paymentCardId);

      toast({
        title: "درخواست جایزه ثبت شد",
        description: response.message,
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      await Promise.all([
        queryClient.invalidateQueries(["club", "me"]),
        queryClient.invalidateQueries(["club", "history"]),
        queryClient.invalidateQueries(["club", "rewards"]),
        queryClient.invalidateQueries(["paymentCards"]),
      ]);
    } catch (error: any) {
      toast({
        title: "بازخرید انجام نشد",
        description:
          error?.response?.data?.message || error?.message || "خطای ناشناخته",
        status: "error",
        duration: 3500,
        isClosable: true,
      });
    }
  };

  if (isLoading) {
    return (
      <VStack w="100%" h="70dvh" justify="center" spacing={4}>
        <Spinner size="xl" color="orange.400" />
        <Text color={mutedColor}>در حال آماده سازی باشگاه مشتریان...</Text>
      </VStack>
    );
  }

  if (isError) {
    return (
      <Alert status="error" borderRadius="2xl">
        <AlertIcon />
        دریافت اطلاعات باشگاه با خطا مواجه شد.
      </Alert>
    );
  }

  if (!isPrivatePerson) {
    return (
      <Alert status="info" borderRadius="2xl">
        <AlertIcon />
        باشگاه مشتریان برای اشخاص حقوقی غیر فعال می باشد
      </Alert>
    );
  }

  return (
    <VStack align="stretch" spacing={6}>
      <Box
        position="relative"
        overflow="hidden"
        borderRadius="2xl"
        px={{ base: 5, md: 8 }}
        py={{ base: 5, md: 6 }}
        bg={heroBg}
        borderWidth="1px"
        borderColor={emptyBorder}
        color={heroTextColor}
      >
        <Box position="absolute" inset={0} background={heroOverlay} />
        <HeroTexture />
        <VStack position="relative" align="stretch" spacing={4}>
          <HStack spacing={2} flexWrap="wrap">
            <Button
              size="sm"
              variant="ghost"
              borderRadius="full"
              bg={subtleBadgeBg}
              color={subtleBadgeColor}
              _hover={{ bg: subtleBadgeBg, opacity: 0.92 }}
              onClick={pointGuideModal.onOpen}
            >
              راهنمای امتیازدهی
            </Button>
            <Button
              size="sm"
              variant="ghost"
              borderRadius="full"
              bg={subtleBadgeBg}
              color={subtleBadgeColor}
              _hover={{ bg: subtleBadgeBg, opacity: 0.92 }}
              onClick={rulesModal.onOpen}
            >
              قوانین کلی
            </Button>
          </HStack>

          <Grid
            templateColumns={{ base: "1fr", xl: "1.25fr 1fr" }}
            gap={{ base: 4, xl: 5 }}
            alignItems="stretch"
          >
            <Box>
              <HStack spacing={3} align="center" mb={3}>
                <Circle
                  size={{ base: "68px", md: "72px" }}
                  bg={avatarRing}
                  color="orange.400"
                  borderWidth="2px"
                  borderColor="orange.400"
                >
                  <Icon as={FiGift as any} boxSize={6} />
                </Circle>
                <VStack align="flex-start" spacing={1}>
                  <Heading size="lg" lineHeight="1.1" color={heroTextColor}>
                    باشگاه مشتریان
                  </Heading>
                  <Text color={softTextColor} fontSize="sm">
                    تجربه ویژه دریافت امتیاز و جایزه
                  </Text>
                </VStack>
              </HStack>
              <Text
                maxW="2xl"
                color={softTextColor}
                fontSize="sm"
                lineHeight="2"
              >
                از مسیر سرمایه گذاری، نگهداری دارایی و معرفی دوستان امتیاز جمع
                کنید، بازخریدهای خود را مدیریت کنید و کارت هدیه را سریع تحویل
                بگیرید.
              </Text>
            </Box>

            <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={3}>
              <HeroInfoCard
                title="تعداد معرفی موفق"
                value={formatPoints(me?.successfulReferralsCount)}
                helper="هر معرفی موفق ۱۰,۰۰۰ امتیاز"
                compact
              />
              <HeroInfoCard
                title="موجودی امتیاز"
                value={`${formatPoints(me?.currentPoints)}`}
                helper={`${formatPoints(rewards.length)} جایزه فعال`}
                compact
              />
            </SimpleGrid>
          </Grid>

          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={3}>
            <Button
              h="auto"
              minH="54px"
              justifyContent="space-between"
              alignItems="center"
              textAlign="right"
              variant="ghost"
              px={4}
              py={3}
              borderRadius="2xl"
              borderWidth="1px"
              borderColor={heroPanelBorder}
              bg={heroPanelCardBg}
              boxShadow="0 10px 24px rgba(15, 23, 42, 0.08)"
              _hover={{
                transform: "translateY(-1px)",
                bg: heroPanelCardHoverBg,
                boxShadow: "0 14px 30px rgba(15, 23, 42, 0.12)",
              }}
              _active={{ transform: "translateY(0)" }}
              onClick={() => handleCopy(me?.referralCode || "", "کد معرفی")}
              isDisabled={!me?.referralCode}
            >
              <VStack align="flex-start" spacing={2} flex="1" minW="0">
                <Text fontSize="xs" color={heroPanelMuted}>
                  کد معرفی
                </Text>
                <Text fontSize="md" fontWeight="900" letterSpacing="widest">
                  {me?.referralCode || "—"}
                </Text>
              </VStack>
              <Text
                fontSize="xs"
                fontWeight="bold"
                color="orange.500"
                flexShrink={0}
              >
                کپی کد
              </Text>
            </Button>
            <Button
              h="auto"
              minH="54px"
              justifyContent="space-between"
              alignItems="center"
              textAlign="right"
              variant="ghost"
              px={4}
              py={3}
              borderRadius="2xl"
              borderWidth="1px"
              borderColor={heroPanelBorder}
              bg={heroPanelCardBg}
              boxShadow="0 10px 24px rgba(15, 23, 42, 0.08)"
              _hover={{
                transform: "translateY(-1px)",
                bg: heroPanelCardHoverBg,
                boxShadow: "0 14px 30px rgba(15, 23, 42, 0.12)",
              }}
              _active={{ transform: "translateY(0)" }}
              onClick={() => handleCopy(me?.referralLink || "", "لینک معرفی")}
              isDisabled={!me?.referralLink}
            >
              <VStack align="flex-start" spacing={2} flex="1" minW="0">
                <Text fontSize="xs" color={heroPanelMuted}>
                  لینک دعوت
                </Text>
                <Text fontSize="sm" whiteSpace="nowrap" overflow="hidden">
                  {me?.referralLink || "—"}
                </Text>
              </VStack>
              <Text
                fontSize="xs"
                fontWeight="bold"
                color="orange.500"
                flexShrink={0}
              >
                کپی لینک
              </Text>
            </Button>
          </SimpleGrid>
        </VStack>
      </Box>

      {latestClaimCardId ? (
        <Box
          borderRadius="2xl"
          p={{ base: 4, md: 5 }}
          bg={claimCtaBg}
          borderWidth="1px"
          borderColor={claimCtaBorder}
        >
          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
            <VStack align="stretch" spacing={2}>
              <HStack spacing={2} color={claimCtaTitle}>
                <Icon as={FiGift as any} boxSize={5} />
                <Text fontWeight="bold">کارت هدیه شما آماده است</Text>
              </HStack>
              <Text color={mutedColor}>
                بازخرید با موفقیت ثبت شد. می توانید همین حالا جزئیات کارت هدیه
                را ببینید یا به صفحه کارت های هدیه بروید.
              </Text>
            </VStack>
            <HStack
              justify={{ base: "stretch", lg: "end" }}
              spacing={3}
              flexWrap="wrap"
            >
              <Button
                colorScheme="orange"
                onClick={() => handleOpenCardDetails(latestClaimCardId)}
                isLoading={isCardLoading}
              >
                مشاهده کارت هدیه
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate(ROUTE_PATHS.DASHBOARD.PAYMENT_CARDS)}
              >
                رفتن به کارت های هدیه
              </Button>
            </HStack>
          </SimpleGrid>
        </Box>
      ) : null}

      <Panel
        title="جوایز و بازخریدها"
        icon={<Box as={FiGift as any} boxSize={5} />}
      >
        <Tabs variant="unstyled">
          <TabList gap={2} flexWrap="wrap">
            {["جوایز قابل دریافت", "تاریخچه بازخریدها"].map((label) => (
              <Tab
                key={label}
                px={4}
                py={2}
                borderRadius="full"
                borderWidth="1px"
                borderColor={cardBorderColor}
                fontWeight="bold"
                color={mutedColor}
                _selected={{
                  bg: "orange.500",
                  color: "white",
                  borderColor: "orange.500",
                  boxShadow: "0 12px 24px rgba(249, 115, 22, 0.22)",
                }}
              >
                {label}
              </Tab>
            ))}
          </TabList>

          <TabPanels px={0}>
            <TabPanel px={0} pt={5}>
              <Tabs variant="unstyled">
                <TabList gap={2} flexWrap="wrap">
                  {["کارت هدیه", "آکادمی امید", "امید یار"].map((label) => (
                    <Tab
                      key={label}
                      px={3}
                      py={2}
                      borderRadius="xl"
                      borderWidth="1px"
                      borderColor={secondaryTabBorder}
                      bg={secondaryTabBg}
                      fontSize="sm"
                      fontWeight="semibold"
                      color={softTextColor}
                      _selected={{
                        bg: secondaryTabSelectedBg,
                        color: secondaryTabSelectedColor,
                        borderColor: "orange.300",
                        boxShadow: secondaryTabShadow,
                      }}
                      _hover={{
                        bg: secondaryTabSelectedBg,
                      }}
                    >
                      {label}
                    </Tab>
                  ))}
                </TabList>

                <TabPanels px={0}>
                  <TabPanel px={0} pt={5}>
                    <VStack align="stretch" spacing={3}>
                      {rewards.length === 0 ? (
                        <Text color={mutedColor}>
                          در حال حاضر جایزه فعالی ثبت نشده است.
                        </Text>
                      ) : (
                        rewards.map((reward: any) => (
                          <Box
                            key={reward._id}
                            borderWidth="1px"
                            borderColor={cardBorderColor}
                            borderRadius="2xl"
                            p={4}
                            bg={subBoxBg}
                          >
                            <SimpleGrid
                              columns={{ base: 1, md: 2 }}
                              spacing={4}
                            >
                              <VStack align="start" spacing={2}>
                                <Text fontWeight="bold">{reward.title}</Text>
                                <Text fontSize="sm" color={mutedColor}>
                                  {reward.description || "بدون توضیح"}
                                </Text>
                                <Badge
                                  colorScheme={
                                    reward.canRedeem ? "green" : "orange"
                                  }
                                >
                                  {reward.canRedeem
                                    ? "قابل دریافت"
                                    : "امتیاز کافی نیست"}
                                </Badge>
                              </VStack>
                              <VStack
                                align={{ base: "start", md: "end" }}
                                spacing={2}
                              >
                                <Text fontSize="sm">
                                  هزینه: {formatPoints(reward.pointsCost)}{" "}
                                  امتیاز
                                </Text>
                                <Text fontSize="sm" color={mutedColor}>
                                  {formatRial(reward?.meta?.amount)}
                                </Text>
                                <Button
                                  colorScheme="orange"
                                  variant={
                                    reward.canRedeem ? "solid" : "outline"
                                  }
                                  onClick={() => handleRedeem(reward._id)}
                                  isDisabled={!reward.canRedeem}
                                >
                                  دریافت جایزه
                                </Button>
                              </VStack>
                            </SimpleGrid>
                          </Box>
                        ))
                      )}
                    </VStack>
                  </TabPanel>

                  <TabPanel px={0} pt={5}>
                    <ComingSoonRewardCard
                      title="اشتراک خرید از آکادمی امید"
                      description="این بخش برای ارائه مزایای اختصاصی آکادمی امید آماده می شود."
                    />
                  </TabPanel>

                  <TabPanel px={0} pt={5}>
                    <ComingSoonRewardCard
                      title="امید یار"
                      description="امکانات تعاملی امید یار باشگاه مشتریان به زودی در این بخش فعال می شود."
                    />
                  </TabPanel>
                </TabPanels>
              </Tabs>
            </TabPanel>

            <TabPanel px={0} pt={5}>
              <VStack align="stretch" spacing={3}>
                {latestRedemptions.length === 0 ? (
                  <Text color={mutedColor}>هنوز بازخریدی ثبت نشده است.</Text>
                ) : (
                  latestRedemptions.map((item: any) => {
                    const meta = rewardStatusMeta[item.status] || {
                      label: item.status || "—",
                      color: "gray",
                    };

                    return (
                      <Box
                        key={item._id}
                        borderWidth="1px"
                        borderColor={cardBorderColor}
                        borderRadius="2xl"
                        p={4}
                        bg={subBoxBg}
                      >
                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                          <VStack align="start" spacing={1}>
                            <Text fontWeight="semibold">
                              {item.reward?.title || "جایزه"}
                            </Text>
                            <Text fontSize="sm" color={mutedColor}>
                              {formatPoints(item.pointsSpent)} امتیاز
                            </Text>
                            <Text fontSize="sm" color={mutedColor}>
                              {formatDate(item.fulfilledAt || item.createdAt)}
                            </Text>
                          </VStack>
                          <VStack
                            align={{ base: "start", md: "end" }}
                            spacing={2}
                          >
                            <Badge colorScheme={meta.color}>{meta.label}</Badge>
                            {item.paymentCard ? (
                              <Button
                                size="sm"
                                variant="outline"
                                rightIcon={
                                  <Icon
                                    as={FiArrowUpRight as any}
                                    boxSize={4}
                                  />
                                }
                                onClick={() =>
                                  handleOpenCardDetails(
                                    item.paymentCard?._id || item.paymentCard,
                                  )
                                }
                              >
                                مشاهده کارت هدیه
                              </Button>
                            ) : null}
                          </VStack>
                        </SimpleGrid>
                      </Box>
                    );
                  })
                )}
              </VStack>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Panel>

      <DataTable
        title="تاریخچه امتیازها"
        columns={historyColumns}
        data={history}
        showAddBtn={false}
        canExport={false}
        totalData={historyTotalData}
        currentPage={historyQuery.data?.currentPage || historyPage}
        pageSize={historyPageSize}
        totalPage={historyTotalPages || 1}
        onPageChange={setHistoryPage}
        canAdd={false}
        canView={false}
        canEdit={false}
        canDelete={false}
      />
      <Modal
        isOpen={pointGuideModal.isOpen}
        onClose={pointGuideModal.onClose}
        size="xl"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>راهنمای امتیازدهی</ModalHeader>
          <ModalCloseButton left={4} right="auto" aria-label="بستن" />
          <ModalBody pb={4}>
            <DataTable
              title="راهنمای امتیازدهی"
              columns={pointGuideColumns}
              data={POINT_GUIDE_ROWS}
              showAddBtn={false}
              canExport={false}
              totalData={POINT_GUIDE_ROWS.length}
              currentPage={1}
              pageSize={POINT_GUIDE_ROWS.length || 1}
              totalPage={1}
              onPageChange={() => {}}
              canAdd={false}
              canView={false}
              canEdit={false}
              canDelete={false}
            />
          </ModalBody>
          <ModalFooter>
            <Button onClick={pointGuideModal.onClose}>بستن</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={rulesModal.isOpen} onClose={rulesModal.onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>قوانین کلی</ModalHeader>
          <ModalCloseButton left={4} right="auto" aria-label="بستن" />
          <ModalBody pb={4}>
            <VStack align="stretch" spacing={3}>
              {GENERAL_RULES.map((rule) => (
                <HStack
                  key={rule}
                  align="start"
                  spacing={3}
                  borderWidth="1px"
                  borderColor={cardBorderColor}
                  borderRadius="2xl"
                  px={3}
                  py={3}
                >
                  <Box
                    mt="7px"
                    flexShrink={0}
                    w="7px"
                    h="7px"
                    borderRadius="full"
                    bg="orange.400"
                  />
                  <Text fontSize="sm">{rule}</Text>
                </HStack>
              ))}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button onClick={rulesModal.onClose}>بستن</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={cardDetailsModal.isOpen}
        onClose={cardDetailsModal.onClose}
        size="4xl"
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>جزئیات کارت هدیه</ModalHeader>
          <ModalCloseButton left={4} right="auto" aria-label="بستن" />
          <ModalBody pb={6}>
            {selectedCard ? (
              <VStack align="stretch" spacing={5}>
                <InvestmentPaymentCard
                  cardNumber={selectedCard.cardNumber}
                  amount={selectedCard.amount}
                  expiresInfo={{
                    label: "انقضا",
                    value: selectedCard.expiresAt,
                  }}
                  status={selectedCard.status}
                  usedAt={selectedCard.usedAt}
                  cancelledAt={
                    selectedCard.cancelledAt ||
                    selectedCard.batchId?.cancelledAt
                  }
                  fundName={
                    selectedCard.fundName ||
                    selectedCard.request?.fundName ||
                    "صندوق سرمایه گذاری"
                  }
                />

                <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={4}>
                  <CardInfoRow
                    label="شماره کارت"
                    value={formatCardNumber(selectedCard.cardNumber)}
                  />
                  <CardInfoRow
                    label="وضعیت"
                    value={
                      <PaymentStatusBadge
                        label={
                          cardStatusMeta[selectedCard.status || ""]?.label ||
                          selectedCard.status ||
                          "—"
                        }
                        colorScheme={
                          cardStatusMeta[selectedCard.status || ""]
                            ?.colorScheme || "gray"
                        }
                      />
                    }
                  />
                  <CardInfoRow
                    label="مبلغ"
                    value={formatRial(selectedCard.amount)}
                  />
                  <CardInfoRow
                    label="صندوق"
                    value={selectedCard.fundName || "—"}
                  />
                  <CardInfoRow
                    label="انقضا"
                    value={formatDate(selectedCard.expiresAt)}
                  />
                  <CardInfoRow
                    label="عنوان بسته"
                    value={selectedCard.batchId?.title || "—"}
                  />
                </Grid>
              </VStack>
            ) : (
              <Text color={mutedColor}>جزئیاتی برای نمایش وجود ندارد.</Text>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </VStack>
  );
};

const CardInfoRow = ({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) => {
  const borderColor = useColorModeValue("gray.100", "whiteAlpha.120");

  return (
    <GridItem
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="2xl"
      p={4}
    >
      <Text fontSize="xs" color="gray.500" mb={2}>
        {label}
      </Text>
      {React.isValidElement(value) ? (
        value
      ) : (
        <Text fontWeight="semibold">{value}</Text>
      )}
    </GridItem>
  );
};

const HeroInfoCard = ({
  title,
  value,
  helper,
  compact = false,
}: {
  title: string;
  value: string;
  helper: string;
  compact?: boolean;
}) => {
  const cardBg = useColorModeValue("whiteAlpha.900", "whiteAlpha.90");
  const borderColor = useColorModeValue("blackAlpha.100", "whiteAlpha.120");
  const titleColor = useColorModeValue("gray.500", "gray.400");
  const valueColor = useColorModeValue("gray.900", "white");
  const helperColor = useColorModeValue("gray.600", "gray.300");

  return (
    <Box
      bg={cardBg}
      borderWidth="1px"
      borderColor={borderColor}
      rounded="2xl"
      p={compact ? 3 : 4}
      backdropFilter="blur(10px)"
      minH={compact ? "120px" : "164px"}
    >
      <VStack
        align="center"
        spacing={compact ? 1 : 2}
        textAlign="center"
        h="100%"
        justify="center"
      >
        <Text fontSize={compact ? "xs" : "sm"} color={titleColor}>
          {title}
        </Text>
        <Text
          fontSize={
            compact ? { base: "xl", md: "2xl" } : { base: "2xl", md: "3xl" }
          }
          fontWeight="900"
          color={valueColor}
        >
          {value}
        </Text>
        <Text
          fontSize="xs"
          color={helperColor}
          lineHeight={compact ? "1.7" : "1.9"}
        >
          {helper}
        </Text>
      </VStack>
    </Box>
  );
};

const ComingSoonRewardCard = ({
  title,
  description,
}: {
  title: string;
  description: string;
}) => {
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const muted = useColorModeValue("gray.600", "gray.300");
  const bg = useColorModeValue("light.400", "dark.700");

  return (
    <Box
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="2xl"
      p={{ base: 5, md: 6 }}
      bg={bg}
    >
      <VStack align="start" spacing={3}>
        <Badge colorScheme="orange" borderRadius="full" px={3} py={1}>
          به زودی
        </Badge>
        <Heading size="md">{title}</Heading>
        <Text color={muted} lineHeight="1.9">
          {description}
        </Text>
      </VStack>
    </Box>
  );
};
